import type { BindingCtx, DomainRead } from "../../../state/editor/types";
import type { Binding, RowDef, SectionDef } from "./schema";

/**
 * Section presets and copy/paste — "save these bloom settings as 'Soft glow'",
 * "copy SSR from this scene, paste into that one".
 *
 * A preset is the section's bound row values keyed by row id. It is applied
 * through each row's own binding, as ONE Undo step (history.recordedBatch), so
 * it is exactly what dragging every slider by hand would have done. Stored per
 * browser in localStorage; a blocked or full storage just means presets do not
 * persist, never an error.
 */

const KEY = "studio.sectionPresets";

export interface SectionPreset {
  name: string;
  values: Record<string, unknown>;
  at: number;
}

type Store = Record<string, SectionPreset[]>;

function load(): Store {
  try {
    const raw = window.localStorage.getItem(KEY);
    const v = raw ? (JSON.parse(raw) as unknown) : null;
    return v && typeof v === "object" ? (v as Store) : {};
  } catch {
    return {};
  }
}

function save(s: Store) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* storage blocked or full — presets stay for this session only */
  }
}

let cache: Store | null = null;
const listeners = new Set<() => void>();

const NONE: SectionPreset[] = [];

/** Stable per state (useSyncExternalStore compares snapshots by identity). */
export function presetsFor(sectionId: string): SectionPreset[] {
  cache ??= load();
  return cache[sectionId] ?? NONE;
}

export function subscribePresets(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function commit(next: Store) {
  cache = next;
  save(next);
  for (const fn of listeners) fn();
}

export function savePreset(sectionId: string, name: string, values: Record<string, unknown>) {
  cache ??= load();
  const list = (cache[sectionId] ?? []).filter((p) => p.name !== name);
  commit({ ...cache, [sectionId]: [...list, { name, values, at: Date.now() }] });
}

export function deletePreset(sectionId: string, name: string) {
  cache ??= load();
  commit({ ...cache, [sectionId]: (cache[sectionId] ?? []).filter((p) => p.name !== name) });
}

type Bound = Extract<RowDef, { binding: unknown }>;

/** The rows a preset carries: bound, shown, enabled, and not editor chrome. */
export function presetRows(section: SectionDef, read: DomainRead, ctx: BindingCtx): Bound[] {
  return section.rows.filter((r): r is Bound => {
    if (!("binding" in r)) return false;
    if ((r.binding as Binding<unknown>).domain === "ui") return false;
    if (r.visible && !r.visible(read, ctx)) return false;
    if (r.disabled && r.disabled(read, ctx) !== false) return false;
    return true;
  });
}

export function captureValues(section: SectionDef, read: DomainRead, ctx: BindingCtx): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const r of presetRows(section, read, ctx)) {
    const v = (r.binding as Binding<unknown>).get(read, ctx);
    // JSON round-trip: what is stored must be what is applied (no live refs).
    out[r.id] = v === undefined ? null : JSON.parse(JSON.stringify(v));
  }
  return out;
}

/** The writes that bring the section to `values` (rows it no longer has are skipped). */
export function writesFor(section: SectionDef, values: Record<string, unknown>, read: DomainRead, ctx: BindingCtx) {
  return presetRows(section, read, ctx)
    .filter((r) => r.id in values)
    .map((r) => ({ binding: r.binding as Binding<unknown>, value: values[r.id] }));
}

/** Copy / Paste settings — one slot, same section only. */
export let copiedSection: { sectionId: string; values: Record<string, unknown> } | null = null;
export function copySection(sectionId: string, values: Record<string, unknown>) {
  copiedSection = { sectionId, values };
}
